/**
 * Automatisk tilldelning (#1653): "Väntar på förvaltare", "Över
 * beloppsgräns – styrelsebeslut" or "Under beloppsgräns – tilldelad
 * förvaltaren" on an issue; nothing when none applies.
 */
import React from 'react';
import { Badge } from 'react-bootstrap';
import { ArrowDownCircle, ArrowUpCircle, Hourglass } from 'lucide-react';
import { Issue } from '../../../types/issues';
import { formatThreshold } from '../../../types/autoAssignment';
import { useIssueAutoAssignment } from '../../../hooks/issues/useIssueAutoAssignment';
import { ISSUE_ASSESSMENT_LABELS, issueAssessmentState } from '../../../Helpers/issueAssessment';

interface IssueAssessmentBadgeProps {
    issue: Pick<Issue, 'createdAt' | 'archivedAt' | 'costAssessment'>;
    /** Detail views: the threshold at decision time and the förvaltare's comment under the badge */
    showDetails?: boolean;
    className?: string;
}

const IssueAssessmentBadge: React.FC<IssueAssessmentBadgeProps> = ({ issue, showDetails = false, className }) => {
    const { setting } = useIssueAutoAssignment();
    const state = issueAssessmentState(issue, setting);
    if (!state) return null;

    const badge = state === 'pending' ? (
        <Badge bg="light" text="dark" className="border d-inline-flex align-items-center gap-1">
            <Hourglass size={10} />
            {ISSUE_ASSESSMENT_LABELS.pending}
        </Badge>
    ) : state === 'above' ? (
        <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1">
            <ArrowUpCircle size={10} />
            {ISSUE_ASSESSMENT_LABELS.above}
        </Badge>
    ) : (
        <Badge bg="success" className="d-inline-flex align-items-center gap-1">
            <ArrowDownCircle size={10} />
            {ISSUE_ASSESSMENT_LABELS.below}
        </Badge>
    );

    const assessment = issue.costAssessment;
    return (
        <div className={className}>
            {badge}
            {showDetails && assessment && (
                <div className="small text-muted mt-1">
                    {`Beloppsgräns ${formatThreshold(assessment.threshold)} kr exkl. moms`}
                    {assessment.comment && (
                        <div className="fst-italic" style={{ whiteSpace: 'pre-wrap' }}>{`Förvaltarens kommentar: "${assessment.comment}"`}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default IssueAssessmentBadge;
